import { ContainerMediaType } from "@/src/store/apis/tasks/taskDetail/interface";
import { DialogConfig, DialogMode, MediaByPhotoType } from "./types";

export const DIALOG_CONFIGS: Record<DialogMode, DialogConfig> = {
  "exporter-shipment": {
    width: 1200,
    showTabs: true,
    showDownloadButton: true,
    showInformation: true,
    showContainerList: true,
    informationFields: [
      "containerNo",
      "sealNo",
      "divider",
      "workingDay",
      "factory",
      "buyer",
      "item",
      "itemCode",
      "netWeight",
    ],
    showIcon: false,
    blockContextMenu: false,
    enableScrollToContainer: false,
    titleKey: "shipment:exporter.dialog.title.ViewPhotosOfContainerWork",
    downloadButtonKey: "shipment:exporter.detail.button.downloadButton",
    tabLabels: {
      container: "shipment:exporter.dialog.tabs.containerType",
      photoType: "shipment:exporter.dialog.tabs.photoType",
    },
  },

  "importer-contract": {
    width: 1200,
    showTabs: true,
    showDownloadButton: true,
    showInformation: true,
    showContainerList: true,
    informationFields: [
      "containerNo",
      "sealNo",
      "divider",
      "workingDay",
      "factory",
      "buyer",
      "item",
      "itemCode",
      "netWeight",
    ],
    showIcon: false,
    blockContextMenu: true,
    enableScrollToContainer: true,
    titleKey: "shipment:importer.dialog.title.ViewPhotosOfContainerWork",
    downloadButtonKey: "shipment:importer.dialog.button.download",
    tabLabels: {
      container: "common:container",
      photoType: "common:photoType",
    },
  },

  "exporter-task": {
    width: 1200,
    showTabs: true,
    showDownloadButton: true,
    showInformation: true,
    showContainerList: true,
    informationFields: [
      "containerNo",
      "sealNo",
      "divider",
      "workingDay",
      "factory",
      "buyer",
      "item",
      "itemCode",
      "netWeight",
    ],
    showIcon: true,
    blockContextMenu: false,
    enableScrollToContainer: false,
    titleKey: "shipment:exporter.dialog.title.ViewPhotosOfContainerWork",
    downloadButtonKey: "shipment:exporter.detail.button.downloadButton",
    tabLabels: {
      container: "shipment:exporter.dialog.tabs.containerType",
      photoType: "shipment:exporter.dialog.tabs.photoType",
    },
  },

  "exporter-single": {
    width: 976,
    showTabs: false,
    showDownloadButton: false,
    showInformation: true,
    showContainerList: false,
    informationFields: [
      "containerNo",
      "sealNo",
      "divider",
      "workingDay",
      "factory",
      "buyer",
      "item",
      "itemCode",
      "netWeight",
    ],
    showIcon: true,
    blockContextMenu: false,
    enableScrollToContainer: false,
    titleKey: "shipment:exporter.dialog.title.ViewPhotosOfContainerWork",
    downloadButtonKey: "",
    tabLabels: {
      container: "",
      photoType: "",
    },
  },

  "shared-email": {
    width: 1200,
    showTabs: true,
    showDownloadButton: true,
    showInformation: true,
    showContainerList: true,
    informationFields: [
      "containerNo",
      "sealNo",
      "divider",
      "buyer",
      "item",
      "netWeight",
    ],
    showIcon: true,
    blockContextMenu: false,
    enableScrollToContainer: true,
    titleKey: "shipment:exporter.dialog.title.ViewPhotosOfContainerWork",
    downloadButtonKey: "shipment:exporter.detail.button.downloadButton",
    tabLabels: {
      container: "common:container",
      photoType: "common:photoType",
    },
  },
};

export const getDialogConfig = (mode: DialogMode): DialogConfig => {
  return DIALOG_CONFIGS[mode];
};

export const getAvailablePhotoTypes = (
  data: MediaByPhotoType | undefined
): string[] => {
  if (!data) return [];

  // 표준 타입 7개 (CLOSE_UP_SEAL, EXTRA 제외 - 고정 위치)
  const standardTypeKeys: Array<{
    key: keyof MediaByPhotoType;
    type: ContainerMediaType;
  }> = [
    { key: "sampleBale", type: "SAMPLE_BALE" },
    { key: "empty", type: "EMPTY" },
    { key: "quarterLoaded", type: "QUARTER_LOADED" },
    { key: "halfLoaded", type: "HALF_LOADED" },
    { key: "fullLoaded", type: "FULL_LOADED" },
    { key: "oneDoorClosed", type: "ONE_DOOR_CLOSED" },
    { key: "bothDoorsClosed", type: "BOTH_DOORS_CLOSED" },
  ];

  const allItems: Array<{ type: string; displayOrder: number }> = [];

  // 표준 타입들 추가 (displayOrder 포함)
  standardTypeKeys.forEach(({ key, type }) => {
    const items = data[key] as Array<{ displayOrder?: number }> | null;
    if (Array.isArray(items) && items.length > 0) {
      const displayOrder = items[0]?.displayOrder ?? 100;
      allItems.push({ type, displayOrder });
    }
  });

  // 커스텀 타입들 추가 (displayOrder 포함)
  if (data.customs && data.customs.length > 0) {
    const customMap = new Map<number, number>();
    data.customs.forEach((c) => {
      if (!customMap.has(c.photoTypeRequestId)) {
        customMap.set(c.photoTypeRequestId, c.displayOrder ?? 100);
      }
    });
    customMap.forEach((displayOrder, id) => {
      allItems.push({ type: `CUSTOM_${id}`, displayOrder });
    });
  }

  // displayOrder로 정렬
  allItems.sort((a, b) => a.displayOrder - b.displayOrder);

  const result = allItems.map((item) => item.type);

  // CLOSE_UP_SEAL: 항상 뒤에서 두 번째 (고정 위치)
  if (data.closeUpSeal && data.closeUpSeal.length > 0) {
    result.push("CLOSE_UP_SEAL");
  }
  // EXTRA: 항상 맨 마지막 (고정 위치)
  if (data.extra && data.extra.length > 0) {
    result.push("EXTRA");
  }

  return result;
};
